import {lazy, ReactElement, Suspense, useMemo} from "react";
import {createBrowserRouter, RouterProvider} from "react-router-dom";

import {Layout} from "@/components/Layout/Layout";
import Loader from "@/components/Loader/Loader";
import {AppContext} from "@/context/AppContext";
import {FournisseurContextProvider} from "@/context/FournisseurContext";
import {HookFormContextProvider} from "@/context/HookFormContext";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {Connexion} from "@/page/Connexion";
import {FournisseurResultat} from "@/page/FournisseurResultat";
import {Rgpd} from "@/page/Rgpd";
import {Fournisseur} from "@/page/Vue_adhérent/Fournisseur";
import {TableauDeBordAdherent} from "@/page/Vue_adhérent/TableauDeBord";
import {Fournisseur as FournisseurVueCollaborateur} from "@/page/Vue_Collaborateur/Fournisseur";
import {HistoriqueKpi} from "@/page/Vue_Collaborateur/HistoriqueKpi";
import {FournisseurTableauDeBord} from "@/page/Vue_fournisseur/FournisseurTableauDeBord";
import {AppContext as AppContextType} from "@/ts/appContext";

const AdministrationKpi = lazy(() =>
  import("@/page/Vue_Collaborateur/AdministrationKpi").then(module => ({
    default: module.AdministrationKpi,
  }))
);

// App.tsx (ou AppRouter.tsx)
export function AppRouter(): ReactElement {
  const {userType} = useGetContext<AppContextType>(AppContext, "AppContext");

  const router = useMemo(() => {
    if (userType === "Adherent") {
      return createBrowserRouter([
        {
          path: "/",
          element: (
            <Layout>
              <TableauDeBordAdherent />
            </Layout>
          ),
        },
        {
          path: "/connexion",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Connexion />
            </Layout>
          ),
        },
        {
          path: "/fournisseurs",
          element: (
            <Layout>
              <FournisseurResultat />
            </Layout>
          ),
        },
        {
          path: "/fournisseur/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: true,
                moduleSelectNeedContremarque: true,
                lockCampagne: true,
                lockContremarque: false,
              }}>
              <FournisseurContextProvider>
                <Fournisseur />
              </FournisseurContextProvider>
            </Layout>
          ),
        },
        {
          path: "/rgpd",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Rgpd />
            </Layout>
          ),
        },
      ]);
    } else if (userType === "Collaborateur") {
      return createBrowserRouter([
        {
          path: "/",
          element: (
            // TODO: activer que lorsque la page Adherent sera disponible

            // <Layout
            //   moduleSelectCampagneContremarqueProps={{
            //     moduleSelectIsVisible: true,
            //     moduleSelectNeedContremarque: false,
            //     lockCampagne: false,
            //     lockContremarque: false,
            //   }}>
            //   <Adherent />
            // </Layout>

            // TODO: retirer quand la page Adherent sera disponible

            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HistoriqueKpi />
            </Layout>
          ),
        },
        {
          path: "/rgpd",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Rgpd />
            </Layout>
          ),
        },
        {
          path: "/connexion",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Connexion />
            </Layout>
          ),
        },
        {
          path: "/fournisseurs",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: true,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <FournisseurResultat />
            </Layout>
          ),
        },
        {
          path: "/fournisseur/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: true,
                moduleSelectNeedContremarque: false,
                lockCampagne: true,
                lockContremarque: false,
              }}>
              <FournisseurContextProvider>
                <HookFormContextProvider>
                  <FournisseurVueCollaborateur />
                </HookFormContextProvider>
              </FournisseurContextProvider>
            </Layout>
          ),
        },
        {
          path: "/historique_kpi",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HistoriqueKpi />
            </Layout>
          ),
        },
        {
          path: "/gestion_kpi/consultation/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HookFormContextProvider>
                <AdministrationKpi isAdminMode={false} />
              </HookFormContextProvider>
            </Layout>
          ),
        },
        {
          path: "/gestion_kpi/creation",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HookFormContextProvider>
                <Suspense fallback={<Loader />}>
                  <AdministrationKpi isAdminMode={true} />
                </Suspense>
              </HookFormContextProvider>
            </Layout>
          ),
        },
        {
          path: "/gestion_kpi/creation/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HookFormContextProvider>
                <Suspense fallback={<Loader />}>
                  <AdministrationKpi isAdminMode={true} />
                </Suspense>
              </HookFormContextProvider>
            </Layout>
          ),
        },
        {
          path: "/gestion_kpi/modification/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <HookFormContextProvider>
                <Suspense fallback={<Loader />}>
                  <AdministrationKpi isAdminMode={false} isUpdate={true} />
                </Suspense>
              </HookFormContextProvider>
            </Layout>
          ),
        },

        {
          path: "/tableau_de_bord/:id",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: true,
                moduleSelectNeedContremarque: true,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <TableauDeBordAdherent />
            </Layout>
          ),
        },
      ]);
    } else if (userType === "Fournisseur") {
      return createBrowserRouter([
        {
          path: "/connexion",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Connexion />
            </Layout>
          ),
        },
        {
          path: "/",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: true,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <FournisseurTableauDeBord />
            </Layout>
          ),
        },
        {
          path: "/rgpd",
          element: (
            <Layout
              moduleSelectCampagneContremarqueProps={{
                moduleSelectIsVisible: false,
                moduleSelectNeedContremarque: false,
                lockCampagne: false,
                lockContremarque: false,
              }}>
              <Rgpd />
            </Layout>
          ),
        },
      ]);
    } else {
      return createBrowserRouter([]);
    }
  }, [userType]);

  return (
    <Suspense fallback={<Loader />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
